import { useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, MagnifyingGlass, ArrowsDownUp, UploadSimple } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { PageHeader, Kpi, Th, Td, Empty, Tag, Segmented, DateRange } from "@/components/common";
import ProductDialog from "@/components/ProductDialog";
import StockDialog from "@/components/StockDialog";
import ImportDialog from "@/components/ImportDialog";
import { AuthImage } from "@/components/AuthImage";
import { api, money, qty, fmtDate, L, monthStart, todayStr } from "@/lib/api";
import { useLoad, useQueryParam } from "@/lib/useLoad";

function MovementsTab() {
  const [start, setStart] = useState(monthStart());
  const [end, setEnd] = useState(todayStr());
  const [rows] = useLoad(() => api.get("/stock/movements", { params: { start, end } }).then((r) => r.data), [start, end]);
  return (
    <div className="panel">
      <div className="border-b border-border p-4"><DateRange start={start} end={end} onStart={setStart} onEnd={setEnd} prefix="stockmoves" /></div>
      <div className="overflow-x-auto">
        <table className="w-full" data-testid="stock-movements-table">
          <thead><tr><Th>Tarih</Th><Th>Ürün</Th><Th>Hareket</Th><Th>Belge</Th><Th>Açıklama</Th><Th right>Giriş</Th><Th right>Çıkış</Th></tr></thead>
          <tbody>
            {rows && rows.length === 0 && <Empty cols={7} />}
            {rows?.map((m) => (
              <tr key={m.id} className="hover:bg-muted/40">
                <Td className="font-num text-xs">{fmtDate(m.date)}</Td>
                <Td><Link to={`/stok/${m.product_id}`} className="hover:text-primary">{m.product_name}</Link></Td>
                <Td><Tag tone={m.qty > 0 ? "collection" : "payment"}>{L.stockMove[m.type]}</Tag></Td>
                <Td className="font-num text-xs">{m.ref_no}</Td>
                <Td className="text-muted-foreground">{m.description}</Td>
                <Td right className="text-emerald-700">{m.qty > 0 ? qty(m.qty) : ""}</Td>
                <Td right className="text-red-600">{m.qty < 0 ? qty(-m.qty) : ""}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function Products() {
  const nav = useNavigate();
  const [tab, setTab] = useState("list");
  const [q, setQ] = useState("");
  const [low, setLow] = useState(false);
  useQueryParam("filtre", useCallback((v) => setLow(v === "kritik"), []), "");
  const [open, setOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [adj, setAdj] = useState(false);
  const [rows, reload] = useLoad(() => api.get("/products", { params: { q: q || undefined, low } }).then((r) => r.data), [q, low]);
  const list = rows || [];

  return (
    <div>
      <PageHeader eyebrow="Ürünler & Envanter" title="Stok Takibi">
        <Button variant="outline" className="rounded-[4px]" onClick={() => setAdj(true)} data-testid="stock-adjust-button"><ArrowsDownUp /> Stok Giriş / Çıkış</Button>
        <Button variant="outline" onClick={() => setImportOpen(true)} className="rounded-[4px]" data-testid="import-products-button"><UploadSimple /> Excel'den Aktar</Button>
        <Button className="rounded-[4px]" onClick={() => setOpen(true)} data-testid="new-product-button"><Plus /> Yeni Ürün</Button>
      </PageHeader>
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Kpi label="Ürün Sayısı" value={list.length} testid="products-count" />
        <Kpi label="Toplam Stok Değeri (Alış)" value={money(list.reduce((s, p) => s + p.stock_value, 0))} tone="blue" testid="products-stock-value" delay={40} />
        <Kpi label="Kritik Stoktaki Ürün" value={list.filter((p) => p.stock <= p.min_stock).length} tone="red" testid="products-low-count" delay={80} />
      </div>
      <div className="mb-4"><Segmented value={tab} onChange={setTab} prefix="stock-tab" options={[["list", "Stok Kartları"], ["moves", "Stok Hareketleri"]]} /></div>
      {tab === "moves" ? <MovementsTab /> : (
        <div className="panel">
          <div className="flex flex-col gap-3 border-b border-border p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative w-full sm:w-72">
              <MagnifyingGlass className="absolute left-2.5 top-2.5 text-muted-foreground" size={16} />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ürün adı veya kod ara…" className="pl-8" data-testid="products-search-input" />
            </div>
            <label className="flex items-center gap-2 text-sm"><Switch checked={low} onCheckedChange={setLow} data-testid="products-low-switch" /> Sadece kritik stok</label>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="products-table">
              <thead><tr><Th>Kod</Th><Th>Ürün</Th><Th>Kategori</Th><Th right>Stok</Th><Th right>Alış</Th><Th right>Satış</Th><Th right>KDV</Th><Th right>Stok Değeri</Th></tr></thead>
              <tbody>
                {rows && list.length === 0 && <Empty cols={8} />}
                {list.map((p) => (
                  <tr key={p.id} onClick={() => nav(`/stok/${p.id}`)} className="cursor-pointer transition-colors hover:bg-muted/50" data-testid={`product-row-${p.id}`}>
                    <Td className="font-num text-xs text-muted-foreground">{p.code}</Td>
                    <Td className="font-medium"><span className="flex items-center gap-2">{p.photo_file_id ? <AuthImage fileId={p.photo_file_id} alt="" className="h-8 w-8 shrink-0 border border-border" testid={`product-thumb-${p.id}`} /> : <span className="h-8 w-8 shrink-0 border border-dashed border-border bg-muted/30" />}<span className="break-all">{p.name}</span></span></Td>
                    <Td className="text-muted-foreground">{p.category || "—"}</Td>
                    <Td right>{p.stock <= p.min_stock ? <Tag tone="low">{qty(p.stock)} {p.unit}</Tag> : <span>{qty(p.stock)} <span className="text-xs text-muted-foreground">{p.unit}</span></span>}</Td>
                    <Td right>{money(p.purchase_price)}</Td>
                    <Td right>{money(p.sale_price)}</Td>
                    <Td right>%{p.vat_rate}</Td>
                    <Td right>{money(p.stock_value)}</Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <ProductDialog open={open} onOpenChange={setOpen} onSaved={reload} />
      <ImportDialog type="products" open={importOpen} onOpenChange={setImportOpen} onImported={reload} />
      <StockDialog open={adj} onOpenChange={setAdj} products={list} onSaved={reload} />
    </div>
  );
}
